import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import { useCallback, useRef } from "react";

/**
 * Hook that creates debounced functions keyed by token
 * Useful for preventing duplicate POST API calls for the same identifier
 */
export const useDebouncedByToken = <TArgs extends any[], TReturn>(
  fn: (...args: TArgs) => TReturn,
  getToken: (...args: TArgs) => string,
  wait?: number,
  options?: Parameters<typeof debounceIfPossible>[2]
) => {
  const debouncedFunctionsRef = useRef<
    Map<string, ReturnType<typeof debounceIfPossible>>
  >(new Map());

  const createDebouncedFunction = useCallback(() => {
    return debounceIfPossible(fn, wait, options);
  }, [fn, wait, options]);

  return useCallback(
    (...args: TArgs): TReturn => {
      const token = getToken(...args);

      // Get or create debounced function for this token
      let debouncedFn = debouncedFunctionsRef.current.get(token);
      if (!debouncedFn) {
        debouncedFn = createDebouncedFunction();
        debouncedFunctionsRef.current.set(token, debouncedFn);
      }

      return debouncedFn(...args);
    },
    [createDebouncedFunction, getToken]
  );
};
